import React from "react";

import AnnotationQuestion from "@uui/AnnotationQuestion";
import { isEmpty } from "lodash/fp";

import Skeleton from "@mui/material/Skeleton";

import {
  CoachingModule,
  CoachingModuleContent,
  CoachingModuleEmptySummary,
  CoachingModuleHeader,
  CoachingModuleSummary,
  CoachingModuleTitle,
} from "@app/analytics/components/CoachingModule";
import ErrorState from "@app/analytics/components/ErrorState";
import Tip from "@app/analytics/components/Tip";
import BenchmarkOverlay, {
  BenchmarkDescription,
} from "@app/analytics/MeetingReport/components/BenchmarkOverlay";
import Moments from "@app/analytics/MeetingReport/components/Moments";
import { Moment } from "@app/analytics/MeetingReport/components/Moments/types";
import { LoadingSkeleton } from "@app/analytics/MeetingReport/styles";
import { TOOLTIPS } from "@app/common/content";
import { pluralizePhrase } from "@app/common/utils";
import { getQuestionsAskedSemanticScore } from "@app/common/utils/coachingUtils";

import { Column } from "@rui/Stack";
import { Bold } from "@rui/styles";

import useCoachingData from "./hooks/useCoachingData";
import InsufficientData from "./InsufficientData";
import { Description } from "./styles";

const QuestionsAskedSummary = ({
  isEmpty,
  isLoading,
  numQuestions,
}: {
  isEmpty: boolean;
  isLoading: boolean;
  numQuestions: number;
}) => {
  if (isLoading) {
    return <Skeleton width={120} />;
  }

  if (isEmpty) {
    return (
      <CoachingModuleEmptySummary>
        Not enough talk time
      </CoachingModuleEmptySummary>
    );
  }

  const semanticScore = getQuestionsAskedSemanticScore(numQuestions);

  return (
    <BenchmarkOverlay
      renderControl={({ onClick }) => (
        <CoachingModuleSummary onClick={onClick} icon={semanticScore}>
          {pluralizePhrase(numQuestions, "question")}
        </CoachingModuleSummary>
      )}
      title="Benchmark"
    >
      <BenchmarkDescription>
        While there's no benchmark for this metric, asking questions helps
        improve communication and builds trust.
      </BenchmarkDescription>
    </BenchmarkOverlay>
  );
};

const QuestionsAskedModule = ({
  onClickMoment,
}: {
  onClickMoment: (m: Moment) => (() => void) | undefined;
}) => {
  const {
    insufficientTalkTimeData,
    isError,
    isInitialLoading,
    questions,
    refetch,
  } = useCoachingData();

  const moments = questions.moments;

  // even if talk time was "too low", still show moments if any were detected
  const showEmptyState = insufficientTalkTimeData && isEmpty(moments);

  return (
    <CoachingModule expandable>
      <CoachingModuleHeader>
        <CoachingModuleTitle
          Icon={AnnotationQuestion}
          tooltip={TOOLTIPS.coaching.questionsAsked}
        >
          Questions asked
        </CoachingModuleTitle>
        <QuestionsAskedSummary
          isEmpty={showEmptyState}
          isLoading={isInitialLoading}
          numQuestions={questions.totalCount}
        />
      </CoachingModuleHeader>
      <CoachingModuleContent>
        {isError ? (
          <ErrorState refetch={refetch} sx={{ minHeight: "150px" }} />
        ) : showEmptyState ? (
          <InsufficientData type="talk_time">
            Your talk time was too low to detect questions.
          </InsufficientData>
        ) : (
          <Column spacing={1.5}>
            {isInitialLoading ? (
              <LoadingSkeleton height={50} />
            ) : isEmpty(moments) ? (
              <Description>
                Read didn't detect that you asked any questions during this
                meeting.
              </Description>
            ) : (
              <>
                <Description>
                  You asked{" "}
                  <Bold>
                    {pluralizePhrase(questions.totalCount, "question")}
                  </Bold>{" "}
                  during this meeting:
                </Description>
                <Moments
                  moments={moments}
                  onClickMoment={onClickMoment}
                  orientation="vertical"
                />
              </>
            )}
            <Tip>
              Questions help show others that you value their input, and keeps
              others engaged in the meeting content.
            </Tip>
          </Column>
        )}
      </CoachingModuleContent>
    </CoachingModule>
  );
};

export default QuestionsAskedModule;
